// Implements: F-ITK-007, F-UIX-007
/**
 * Choose where a linked number creates, ask for a code, and read the command to send. Pure
 * presentation: the request and its answer belong to the container.
 *
 * The sentence with the command and its expiry lives in a `role="status"` region that is in the
 * document from the start, so its arrival is announced (a live region inserted together with its
 * text is not). A refusal is a `role="alert"`.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Select } from "../../../shared/ui/atoms/Select";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import type { LinkScopeOption } from "../model/linking";
import { IssuedCommand } from "./IssuedCommand";
import styles from "./WhatsApp.module.css";

export const LINK_SCOPE_FIELD_ID = "intake-link-scope";

export interface IssuedCommandText {
  readonly command: string;
  /** The expiry, already written for a person. */
  readonly expires: string;
}

export interface LinkCodeFormProps {
  readonly options: readonly LinkScopeOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly pending: boolean;
  /** The refusal to show, already written for a person. */
  readonly error: string | null;
  readonly issued: IssuedCommandText | null;
}

export function LinkCodeForm({
  options,
  value,
  onChange,
  onSubmit,
  pending,
  error,
  issued,
}: LinkCodeFormProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <h2 className={styles.subtitle}>{t("intake.issue.title")}</h2>
      {options.length === 0 ? (
        <p className={styles.body}>{t("intake.issue.noScopes")}</p>
      ) : (
        <div className={styles.row}>
          <Field id={LINK_SCOPE_FIELD_ID} label={t("intake.issue.scope")}>
            <Select
              id={LINK_SCOPE_FIELD_ID}
              value={value}
              options={options}
              describedBy={describedBy(LINK_SCOPE_FIELD_ID, {})}
              onChange={onChange}
            />
          </Field>
          <Button variant="primary" loading={pending} disabled={pending} onClick={onSubmit}>
            {t("intake.issue.submit")}
          </Button>
        </div>
      )}
      {error === null ? null : (
        <p role="alert" className={styles.notice}>
          {error}
        </p>
      )}
      <p role="status" className={styles.notice}>
        {issued === null ? null : t("intake.code.issued", { command: issued.command, when: issued.expires })}
      </p>
      {issued === null ? null : <IssuedCommand key={issued.command} command={issued.command} />}
    </div>
  );
}
